/**
 * 🐛 오류 제보 게시판 (점주/직원)
 * 백엔드: /api/v1/owner/error-reports/*
 *
 * - 오류를 제목 + 설명 + 스크린샷(여러 장)으로 제보
 * - 제보 시 자동으로 브라우저/화면 정보를 함께 수집(디버깅용)
 * - "내 제보 이력"에서 처리 상태와 본부(대표) 답변 확인
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../infrastructure/api';

const CATEGORIES = [
  '화면이 안 보임/깨짐',
  '버튼이 안 눌림/동작 안 함',
  '숫자·데이터가 이상함',
  '알림톡 관련',
  '결제/매출 관련',
  '기타',
];

const STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  open: { label: '접수됨', bg: '#fff3cd', fg: '#856404' },
  reviewing: { label: '확인중', bg: '#cfe2ff', fg: '#084298' },
  in_progress: { label: '수정중', bg: '#ffe5d0', fg: '#8a3d00' },
  resolved: { label: '해결됨', bg: '#d1e7dd', fg: '#0f5132' },
  not_a_bug: { label: '정상 동작', bg: '#e2e3e5', fg: '#41464b' },
  need_info: { label: '추가 정보 필요', bg: '#f8d7da', fg: '#842029' },
};

interface ReportListItem {
  id: string;
  title: string;
  category?: string;
  status: string;
  has_reply: boolean;
  created_at: string;
  attachment_count: number;
}

interface AttachmentMeta {
  id: string;
  filename?: string;
  mime?: string;
  size?: number;
}

interface ReportDetail {
  id: string;
  title: string;
  content: string;
  category?: string;
  page_url?: string;
  status: string;
  admin_reply?: string;
  created_at: string;
  attachments: AttachmentMeta[];
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const m = STATUS_META[status] || { label: status, bg: '#e2e3e5', fg: '#41464b' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600, backgroundColor: m.bg, color: m.fg,
    }}>{m.label}</span>
  );
};

/** 인증이 필요한 첨부 이미지를 blob 으로 받아 표시 */
const AuthImage: React.FC<{ reportId: string; attId: string; alt?: string }> = ({ reportId, attId, alt }) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    apiClient
      .get(`/api/v1/owner/error-reports/${reportId}/attachments/${attId}`, { responseType: 'blob' })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [reportId, attId]);
  if (!src) return <div style={{ width: 120, height: 90, background: '#f1f1f1', borderRadius: 6 }} />;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt={alt || 'screenshot'}
        style={{ maxWidth: 200, maxHeight: 160, borderRadius: 6, border: '1px solid #ddd', objectFit: 'contain' }} />
    </a>
  );
};

const ErrorReportsPage: React.FC = () => {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReportDetail | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/v1/owner/error-reports');
      setReports(res.data?.reports || []);
    } catch (e) {
      console.error('오류 제보 목록 조회 실패', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    const images = picked.filter((f) => f.type.startsWith('image/'));
    const next = [...files, ...images].slice(0, 5);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { alert('제목을 입력해주세요.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('content', content);
      if (category) fd.append('category', category);
      fd.append('page_url', document.referrer || '');
      fd.append('app_context', JSON.stringify({
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        screen: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        at: new Date().toISOString(),
      }));
      files.forEach((f) => fd.append('files', f));
      await apiClient.post('/api/v1/owner/error-reports', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('오류 제보가 접수되었습니다. 확인 후 처리해 드리겠습니다.');
      setTitle(''); setCategory(''); setContent(''); setFiles([]); setPreviews([]);
      await fetchReports();
    } catch (err: any) {
      alert(`제보 접수 실패: ${err?.response?.data?.detail || err?.message || '알 수 없는 오류'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDetail = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(id); setDetail(null);
    try {
      const res = await apiClient.get(`/api/v1/owner/error-reports/${id}`);
      setDetail(res.data?.report || null);
    } catch (e) {
      console.error('상세 조회 실패', e);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: '1px solid #d5d5d5',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: 20 }}>
      <div style={{ marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>🐛 오류 제보</h1>
        <p style={{ color: '#666', marginTop: 6, fontSize: 14, lineHeight: 1.6 }}>
          사용 중 이상한 점이나 오류를 발견하면 알려주세요.<br />
          <b>스크린샷을 함께 올려주시면</b> 훨씬 빠르고 정확하게 확인·수정할 수 있습니다.
        </p>
      </div>

      {/* 제보 폼 */}
      <form onSubmit={handleSubmit}
        style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, marginBottom: 28,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>제목 *</label>
          <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="예: 출석부에서 특정 학생이 계속 미출석으로 떠요" maxLength={200} />
        </div>

        <div>
          <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>분류</label>
          <select style={inputStyle} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">선택 안 함</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>설명</label>
          <textarea style={{ ...inputStyle, minHeight: 100, fontFamily: 'inherit', resize: 'vertical' }}
            value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="어떤 화면에서, 무엇을 했더니, 어떻게 됐는지 적어주세요. (언제부터 그런지도 도움돼요)" />
        </div>

        <div>
          <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>
            스크린샷 (최대 5장, 선택)
          </label>
          <input type="file" accept="image/*" multiple onChange={onPickFiles} />
          {previews.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
              {previews.map((src, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={src} alt={`preview-${i}`}
                    style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                  <button type="button" onClick={() => removeFile(i)}
                    style={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: 999,
                      border: 'none', background: '#dc3545', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: '22px' }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button type="submit" disabled={submitting}
          style={{ alignSelf: 'flex-start', padding: '11px 22px', background: submitting ? '#9db8f0' : '#2563eb',
            color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer' }}>
          {submitting ? '접수 중…' : '오류 제보하기'}
        </button>
      </form>

      {/* 내 제보 이력 */}
      <h2 style={{ fontSize: 17, fontWeight: 800, marginBottom: 12 }}>내 제보 이력</h2>
      {loading ? (
        <p style={{ color: '#888' }}>불러오는 중…</p>
      ) : reports.length === 0 ? (
        <p style={{ color: '#999' }}>아직 제보한 오류가 없습니다.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.map((r) => (
            <div key={r.id} style={{ border: '1px solid #eee', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
              <button onClick={() => toggleDetail(r.id)}
                style={{ width: '100%', textAlign: 'left', padding: '14px 16px', background: 'none', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.title}
                    {r.attachment_count > 0 && <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>📷 {r.attachment_count}</span>}
                    {r.has_reply && <span style={{ marginLeft: 8, fontSize: 12, color: '#0f5132' }}>💬 답변</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 3 }}>
                    {r.category ? `${r.category} · ` : ''}{new Date(r.created_at).toLocaleString('ko-KR')}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </button>

              {expandedId === r.id && (
                <div style={{ borderTop: '1px solid #f0f0f0', padding: 16, background: '#fafafa' }}>
                  {!detail ? <p style={{ color: '#999' }}>불러오는 중…</p> : (
                    <>
                      {detail.content && (
                        <p style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6, margin: '0 0 12px' }}>{detail.content}</p>
                      )}
                      {detail.attachments.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                          {detail.attachments.map((a) => (
                            <AuthImage key={a.id} reportId={detail.id} attId={a.id} alt={a.filename} />
                          ))}
                        </div>
                      )}
                      {detail.admin_reply && (
                        <div style={{ background: '#eef6ff', border: '1px solid #cfe2ff', borderRadius: 8, padding: 12 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#084298', marginBottom: 4 }}>💬 본부 답변</div>
                          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{detail.admin_reply}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ErrorReportsPage;
